import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PricingInfo from '../components/PricingInfo';

const HomePage = ({ user }) => {
  const [showPricing, setShowPricing] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user) {
      if (user.role === 'chef') {
        navigate('/chef/dashboard');
      } else if (user.role === 'customer') {
        navigate('/customer/dashboard');
      } else if (user.role === 'admin') {
        navigate('/admin/dashboard');
      }
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6" data-testid="hero-title">
            Professional Private Chefs, 
            <br />
            Delivered to Your Home
          </h1>
          <p className="text-xl mb-8 text-white/90">
            Book experienced chefs for your next dinner party, event, or special occasion.
          </p>
          <div className="flex justify-center gap-4">
            <Link
              to="/signup/customer"
              className="px-8 py-4 bg-white text-orange-600 rounded-lg font-semibold hover:bg-gray-100 text-lg"
              data-testid="book-chef-button"
            >
              Book a Chef
            </Link>
            <Link
              to="/signup/chef"
              className="px-8 py-4 bg-orange-800 text-white rounded-lg font-semibold hover:bg-orange-900 text-lg"
              data-testid="become-chef-button"
            >
              Become a Chef
            </Link>
          </div>
          
          {/* Social Media */}
          <div className="mt-8 flex justify-center gap-6">
            <a 
              href="https://facebook.com/chef2gonow" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white/90 hover:text-white transition"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Follow on Facebook
            </a>
            <a 
              href="https://instagram.com/chef2_Go_Now" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white/90 hover:text-white transition"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              Follow on Instagram
            </a>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">How It Works</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-5xl mb-4">👨‍🍳</div>
              <h3 className="text-xl font-semibold mb-2">Browse Chefs</h3>
              <p className="text-gray-600">Choose from our curated selection of professional chefs with diverse culinary expertise.</p>
            </div>
            
            <div className="text-center">
              <div className="text-5xl mb-4">📝</div>
              <h3 className="text-xl font-semibold mb-2">Book Your Experience</h3>
              <p className="text-gray-600">Select your meal type, guest count, and preferred date. We handle the rest!</p>
            </div>
            
            <div className="text-center">
              <div className="text-5xl mb-4">🍴</div>
              <h3 className="text-xl font-semibold mb-2">Enjoy Amazing Food</h3>
              <p className="text-gray-600">Sit back and enjoy restaurant-quality meals in the comfort of your home.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Transparent Pricing</h2>
            <p className="text-xl text-gray-600">No hidden fees. Clear, upfront pricing.</p>
            <button
              onClick={() => setShowPricing(!showPricing)}
              className="mt-4 px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
            >
              {showPricing ? 'Hide Pricing' : 'View Pricing Details'}
            </button>
          </div>
          
          {showPricing && (
            <div className="max-w-4xl mx-auto">
              <PricingInfo />
            </div>
          )}
          
          {!showPricing && (
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white rounded-lg shadow-lg p-6 text-center">
                <div className="text-4xl mb-3">🍽️</div>
                <h3 className="font-bold text-xl mb-2">Dinner</h3>
                <p className="text-2xl font-bold text-orange-600">Starting at $425</p>
                <p className="text-sm text-gray-600">for 2 people</p>
                <p className="text-xs text-gray-500 mt-2">+ $26 fee + groceries</p>
              </div>
              
              <div className="bg-white rounded-lg shadow-lg p-6 text-center">
                <div className="text-4xl mb-3">🥗</div>
                <h3 className="font-bold text-xl mb-2">Lunch</h3>
                <p className="text-2xl font-bold text-blue-600">Starting at $175</p>
                <p className="text-sm text-gray-600">for 2 people</p>
                <p className="text-xs text-gray-500 mt-2">+ $26 fee + groceries</p>
              </div>
              
              <div className="bg-white rounded-lg shadow-lg p-6 text-center">
                <div className="text-4xl mb-3">🛒</div>
                <h3 className="font-bold text-xl mb-2">Groceries</h3>
                <p className="text-lg font-bold text-purple-600">Actual Cost</p>
                <p className="text-sm text-gray-600">receipts verified</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-gray-600 mb-8">
            Join thousands of satisfied customers who have experienced the magic of private chefs.
          </p>
          <Link
            to="/signup/customer"
            className="inline-block px-8 py-4 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 text-lg"
            data-testid="cta-signup-button"
          >
            Book Your First Chef
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
